import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["aria-describedby", "aria-invalid", "aria-required", "calendarButtonAltText", "clearButtonAltText", "collapsedCloseButtonAltText", "collapsedTitleText", "dateMarkerLegend", "defaultValue", "disabled", "error", "id", "inputProps", "labelText", "locale", "name", "onChange", "popoverProps", "renderPopupContent", "shouldCloseOnDateSelection", "showMilliseconds", "showSeconds", "showTime", "value", "warning"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useContext, useEffect, useRef, useState } from 'react';
import { describe, PropTypes } from '@amzn/stencil-react-desc';
import { StencilContext } from "../../context";
import { Label } from "../../text";
import { LABEL_COLOR, LABEL_ERROR_COLOR, omit, pickDataProps, withTestAttributes } from "../../utils";
import { getTimeMaskPlaceholder } from "../time-picker/utils";
import DatePicker from "../DatePicker";
import { DEFAULT_DATE_FORMAT } from "../constants";
import { getDateFormat } from "../utils";
import DateInputWithPopup from "./DateInputWithPopup";
import { defaultPopupDatePickerProps } from "./constants";
import { LabelWrapperCol } from "./helperComponents";
import { basePopupDatePickerPropTypes, forwardedInputPropTypes } from "./propTypes";
import { jsx as ___EmotionJSX } from "@emotion/core";

var PopupDatePickerWithoutDocs = function PopupDatePickerWithoutDocs(_ref) {
  var ariaDescribedBy = _ref['aria-describedby'],
      ariaInvalid = _ref['aria-invalid'],
      ariaRequired = _ref['aria-required'],
      _ref$calendarButtonAl = _ref.calendarButtonAltText,
      calendarButtonAltText = _ref$calendarButtonAl === void 0 ? defaultPopupDatePickerProps.calendarButtonAltText : _ref$calendarButtonAl,
      clearButtonAltText = _ref.clearButtonAltText,
      _ref$collapsedCloseBu = _ref.collapsedCloseButtonAltText,
      collapsedCloseButtonAltText = _ref$collapsedCloseBu === void 0 ? defaultPopupDatePickerProps.collapsedCloseButtonAltText : _ref$collapsedCloseBu,
      _ref$collapsedTitleTe = _ref.collapsedTitleText,
      collapsedTitleText = _ref$collapsedTitleTe === void 0 ? defaultPopupDatePickerProps.collapsedTitleText : _ref$collapsedTitleTe,
      dateMarkerLegend = _ref.dateMarkerLegend,
      defaultValue = _ref.defaultValue,
      disabled = _ref.disabled,
      error = _ref.error,
      id = _ref.id,
      _ref$inputProps = _ref.inputProps,
      inputProps = _ref$inputProps === void 0 ? defaultPopupDatePickerProps.inputProps : _ref$inputProps,
      labelText = _ref.labelText,
      locale = _ref.locale,
      name = _ref.name,
      onChange = _ref.onChange,
      _ref$popoverProps = _ref.popoverProps,
      popoverProps = _ref$popoverProps === void 0 ? defaultPopupDatePickerProps.popoverProps : _ref$popoverProps,
      _ref$renderPopupConte = _ref.renderPopupContent,
      renderPopupContent = _ref$renderPopupConte === void 0 ? defaultPopupDatePickerProps.renderPopupContent : _ref$renderPopupConte,
      shouldCloseOnDateSelection = _ref.shouldCloseOnDateSelection,
      showMilliseconds = _ref.showMilliseconds,
      showSeconds = _ref.showSeconds,
      showTime = _ref.showTime,
      value = _ref.value,
      warning = _ref.warning,
      datePickerProps = _objectWithoutProperties(_ref, _excluded);

  var _useContext = useContext(StencilContext),
      stencilLocale = _useContext.locale;

  var _useState = useState(value !== undefined ? value : defaultValue),
      _useState2 = _slicedToArray(_useState, 2),
      stateValue = _useState2[0],
      setStateValue = _useState2[1];

  var _useState3 = useState(''),
      _useState4 = _slicedToArray(_useState3, 2),
      shouldFocusWithValue = _useState4[0],
      setShouldFocusWithValue = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      invalid = _useState6[0],
      setInvalid = _useState6[1];

  var _useState7 = useState(error || !!inputProps.error),
      _useState8 = _slicedToArray(_useState7, 2),
      hasError = _useState8[0],
      setHasError = _useState8[1];

  var inputRef = useRef(null);

  if (!locale) {
    locale = stencilLocale;
  }

  var internalValue = value !== undefined ? value : stateValue;
  var isDateDisabled = datePickerProps.isDateDisabled;
  var showTimeProps = {
    showMilliseconds: showMilliseconds,
    showSeconds: showSeconds,
    showTime: showTime
  };

  var handleChange = function handleChange(value, closePopup) {
    setHasError(false);
    setInvalid(false);
    setStateValue(value);
    !!onChange && onChange(value);

    if (!!closePopup && (shouldCloseOnDateSelection !== undefined ? shouldCloseOnDateSelection : !showTime)) {
      closePopup();
    }
  };

  var handleDatePickerChange = function handleDatePickerChange(value, closePopup) {
    handleChange(value, closePopup);

    if (!showTime) {
      setShouldFocusWithValue(value);
    }
  };

  var handleSetInvalid = function handleSetInvalid() {
    setInvalid(true);
  }; // Check if value has been set when invalid


  useEffect(function () {
    if (invalid && value !== undefined && value !== stateValue) {
      setInvalid(false);
    }
  }, [invalid, stateValue, value]); // Check if a disabled date has been selected

  useEffect(function () {
    if (!!isDateDisabled && !!stateValue) {
      var format = getDateFormat(locale) || DEFAULT_DATE_FORMAT;

      if (stateValue.length >= format.length) {
        var testDate = new Date(stateValue);

        if (!isNaN(testDate.valueOf()) && isDateDisabled(stateValue)) {
          setHasError(true);
          setInvalid(true);
        }
      }
    }
  }, [isDateDisabled, locale, stateValue]);

  var renderPopup = function renderPopup(closePopup) {
    var calendar = ___EmotionJSX(DatePicker, _extends({}, datePickerProps, showTimeProps, {
      locale: locale,
      onChange: function onChange(value) {
        return handleDatePickerChange(value, closePopup);
      },
      value: invalid ? '' : internalValue
    }));

    return renderPopupContent({
      calendar: calendar,
      legend: dateMarkerLegend
    });
  };

  var dataProps = pickDataProps(datePickerProps);

  var forwardedInputProps = _objectSpread(_objectSpread({
    'aria-describedby': ariaDescribedBy,
    'aria-invalid': ariaInvalid || invalid,
    'aria-required': ariaRequired,
    error: error || hasError,
    id: id,
    locale: locale,
    maskPlaceholder: getTimeMaskPlaceholder(getDateFormat(locale), showTimeProps),
    name: name,
    setShouldFocusWithValue: setShouldFocusWithValue,
    shouldFocusWithValue: shouldFocusWithValue,
    value: internalValue,
    warning: warning
  }, showTimeProps), inputProps);

  return ___EmotionJSX(LabelWrapperCol, null, labelText && ___EmotionJSX(Label, {
    color: inputProps.error || hasError ? LABEL_ERROR_COLOR : LABEL_COLOR,
    htmlFor: id
  }, labelText), ___EmotionJSX(DateInputWithPopup, _extends({
    calendarButtonAltText: calendarButtonAltText,
    clearButtonAltText: clearButtonAltText,
    collapsedCloseButtonAltText: collapsedCloseButtonAltText,
    collapsedTitleText: collapsedTitleText,
    disabled: disabled,
    inputProps: forwardedInputProps,
    inputRef: inputRef,
    onInputChange: handleChange,
    onInputError: handleSetInvalid,
    popoverProps: popoverProps,
    renderPopupContent: renderPopup
  }, dataProps)));
};

var PopupDatePickerWithDocs = describe(PopupDatePickerWithoutDocs).description('Date picker shown in a popup.');
PopupDatePickerWithDocs.propTypes = _objectSpread(_objectSpread(_objectSpread({}, omit(DatePicker.describedPropTypes, ['aria-describedby', 'aria-required', 'error'])), basePopupDatePickerPropTypes), {}, {
  inputProps: PropTypes.shape(forwardedInputPropTypes).description('Props forwarded to the input field.'),
  showMilliseconds: PropTypes.bool.description("Determines if milliseconds should be added to the time in the picker and input. Only works when 'showSeconds' is true"),
  showSeconds: PropTypes.bool.description("Determines if seconds should be added to the time in the picker and input. Only works when 'showTime' is true"),
  showTime: PropTypes.bool.description('Determines if time should be included in the picker and input.')
});
var PopupDatePicker = withTestAttributes({
  component: 'StencilPopupDatePicker'
})(PopupDatePickerWithDocs);
PopupDatePicker.defaultProps = _objectSpread({}, defaultPopupDatePickerProps);
PopupDatePicker.displayName = 'PopupDatePicker';
export default PopupDatePicker;